import React, { useState } from 'react';
import { API_BASE_URL, getAuthHeaders } from '../../config/api.config';

const AuthTest: React.FC = () => {
  const [results, setResults] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  const addResult = (message: string, isError = false) => {
    setResults(prev => [...prev, `${isError ? '❌' : '✅'} ${message}`]);
  };

  const clearResults = () => {
    setResults([]);
  };

  const checkCurrentAuth = () => {
    clearResults();
    const token = localStorage.getItem('authToken');
    const user = localStorage.getItem('user');
    
    addResult(`Token: ${token ? 'Found (' + token.substring(0, 30) + '...)' : 'NOT FOUND'}`, !token);
    addResult(`User: ${user ? 'Found' : 'NOT FOUND'}`, !user);
    
    if (user) {
      try {
        const userData = JSON.parse(user);
        addResult(`User Email: ${userData.email}`);
        
        // Check role structure
        let actualRole = userData.role;
        if (typeof actualRole === 'object' && actualRole !== null) {
          // Handle object role (might be {name: 'admin'} or similar)
          actualRole = actualRole.name || JSON.stringify(actualRole);
          addResult(`Role is object: ${JSON.stringify(userData.role)}`, true);
        }
        
        addResult(`User Role: ${actualRole || 'undefined'}`, actualRole !== 'admin');
        addResult(`Is Admin: ${actualRole === 'admin' ? 'YES' : 'NO'}`, actualRole !== 'admin');
      } catch (e) {
        addResult('Error parsing user data', true);
      }
    }
  };

  const fixUserRole = () => {
    clearResults();
    const user = localStorage.getItem('user');
    
    if (!user) {
      addResult('No user data found to fix', true);
      return;
    }
    
    try {
      const userData = JSON.parse(user);
      
      // Fix role if it's an object
      if (typeof userData.role === 'object' && userData.role !== null) {
        // Extract role name if it's an object
        const roleName = userData.role.name || 'admin';
        userData.role = roleName;
        
        // Save fixed user data
        localStorage.setItem('user', JSON.stringify(userData));
        addResult(`Fixed role: Changed from object to "${roleName}"`);
      } else if (!userData.role) {
        // Set admin role if missing
        userData.role = 'admin';
        localStorage.setItem('user', JSON.stringify(userData));
        addResult('Added missing admin role');
      } else {
        addResult(`Role is already correct: ${userData.role}`);
      }
      
      // Verify fix
      checkCurrentAuth();
    } catch (e) {
      addResult('Error fixing user data', true);
    }
  };

  const testLogin = async () => {
    clearResults();
    setLoading(true);
    
    try {
      addResult('Testing login...');
      
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          email: 'ken.peluso@roiconstruction.com',
          password: 'password123',
        }),
      });
      
      addResult(`Response Status: ${response.status}`, response.status !== 200);
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        // Store auth data
        localStorage.setItem('authToken', data.data.access_token);
        localStorage.setItem('user', JSON.stringify({
          id: data.data.user.id,
          name: data.data.user.name,
          email: data.data.user.email,
          role: data.data.user.roles && data.data.user.roles.length > 0 ? data.data.user.roles[0] : 'user',
        }));
        
        addResult('Login successful!');
        addResult(`User: ${data.data.user.email}`);
        addResult(`Role: ${data.data.user.roles?.[0] || 'user'}`);
      } else {
        addResult(`Login failed: ${data.message || 'Unknown error'}`, true);
      }
    } catch (error: any) {
      addResult(`Login error: ${error.message}`, true);
    } finally {
      setLoading(false);
    }
  };

  const testBusinessRulesAPI = async () => {
    clearResults();
    setLoading(true);
    
    const token = localStorage.getItem('authToken');
    if (!token) {
      addResult('No auth token found. Please login first.', true);
      setLoading(false);
      return;
    }
    
    try {
      addResult('Testing Business Rules API...');
      addResult(`Using token: ${token.substring(0, 30)}...`);
      
      const response = await fetch(`${API_BASE_URL}/v2/admin/business-rules?report_type=exteriors`, {
        method: 'GET',
        headers: getAuthHeaders() as HeadersInit,
      });
      
      addResult(`Response Status: ${response.status}`, response.status !== 200);
      
      if (response.ok) {
        const data = await response.json();
        addResult('Business Rules API working!');
        addResult(`Commission Tiers: ${data.data?.commission_tiers?.length || 0}`);
        addResult(`Manager Fee: ${data.data?.fees?.manager_fee_percent || 0}%`);
        addResult(`Company Overhead: ${data.data?.fees?.company_overhead_percent || 0}%`);
      } else {
        const errorText = await response.text();
        addResult(`API Error: ${errorText}`, true);
      }
    } catch (error: any) {
      addResult(`API error: ${error.message}`, true);
    } finally {
      setLoading(false);
    }
  };

  const runFullDiagnostic = async () => {
    clearResults();
    setLoading(true);
    
    try {
      // 1. Check current auth
      checkCurrentAuth();
      
      // 2. Test backend connectivity
      addResult('--- Testing Backend ---');
      try {
        const response = await fetch(`${API_BASE_URL}/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: 'test', password: 'test' }),
        });
        addResult(`Auth endpoint accessible (Status: ${response.status})`);
      } catch (e) {
        addResult('Cannot reach auth endpoint', true);
      }
      
      // 3. Test admin endpoints
      const token = localStorage.getItem('authToken');
      if (token) {
        try {
          const response = await fetch(`${API_BASE_URL}/v2/admin/business-rules?report_type=exteriors`, {
            headers: getAuthHeaders() as HeadersInit,
          });
          
          if (response.status === 200) {
            addResult('Admin access confirmed');
          } else if (response.status === 401) {
            addResult('Auth token invalid or expired', true);
          } else if (response.status === 403) {
            addResult('User does not have admin role', true);
          } else {
            addResult(`Unexpected status: ${response.status}`, true);
          }
        } catch (e) {
          addResult('Admin API test failed', true);
        }
      }
      
      // 4. Check API configuration
      addResult('--- API Configuration ---');
      addResult(`API Base URL: ${API_BASE_URL}`);
      addResult(`Using Proxy: ${window.location.hostname === 'localhost' ? 'YES' : 'NO'}`);
      
    } catch (error: any) {
      addResult(`Diagnostic error: ${error.message}`, true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">Authentication & API Test</h1>
      
      <div className="space-y-4">
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Quick Actions</h2>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={checkCurrentAuth}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Check Auth Status
            </button>
            <button
              onClick={fixUserRole}
              className="px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700"
            >
              Fix User Role
            </button>
            <button
              onClick={testLogin}
              disabled={loading}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-400"
            >
              Test Login
            </button>
            <button
              onClick={testBusinessRulesAPI}
              disabled={loading}
              className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:bg-gray-400"
            >
              Test Business Rules API
            </button>
            <button
              onClick={runFullDiagnostic}
              disabled={loading}
              className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 disabled:bg-gray-400"
            >
              Run Full Diagnostic
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Results</h2>
          {loading && (
            <div className="text-blue-600 mb-2">Running tests...</div>
          )}
          {results.length === 0 ? (
            <p className="text-gray-500">Click a button above to run tests</p>
          ) : (
            <div className="space-y-1">
              {results.map((result, index) => (
                <div
                  key={index}
                  className={`font-mono text-sm ${
                    result.startsWith('❌') ? 'text-red-600' : 
                    result.startsWith('✅') ? 'text-green-600' : 
                    'text-gray-800'
                  }`}
                >
                  {result}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-blue-50 rounded-lg p-4">
          <h3 className="font-semibold text-blue-900 mb-2">Quick Fix Guide</h3>
          <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
            <li>Make sure Laravel backend is running: <code className="bg-blue-100 px-1">php artisan serve --port=3001</code></li>
            <li>Click &quot;Test Login&quot; to authenticate</li>
            <li>Click &quot;Test Business Rules API&quot; to verify admin access</li>
            <li>If errors persist, check Laravel logs: <code className="bg-blue-100 px-1">tail -f storage/logs/laravel.log</code></li>
          </ol>
        </div>
      </div>
    </div>
  );
};

export default AuthTest;